import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { clientsApi, businessesApi, customFieldsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { CreateBusinessRequest, UpdateBusinessRequest, FieldConfiguration } from '@/types'
import { validateRegex, getApiErrorMessage, isValidEmail, sanitizePhoneNumber } from '@/lib/utils'

const currencies = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'SGD', name: 'Singapore Dollar' },
]

interface BusinessFormPageProps {
  mode?: 'view' | 'edit'
}

export default function BusinessFormPage({ mode: propMode }: BusinessFormPageProps = {}) {
  const navigate = useNavigate()
  const { clientId, businessId } = useParams<{ clientId: string; businessId?: string }>()
  const queryClient = useQueryClient()

  // Determine mode
  const isViewMode = propMode === 'view' || (!!businessId && propMode !== 'edit')
  const isCreateMode = !businessId && !propMode
  const [isEditing, setIsEditing] = useState(!isViewMode)

  const { data: client } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.getById(clientId!),
    enabled: !!clientId,
  })

  const { data: existingBusiness, isLoading: isLoadingBusiness } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => businessesApi.getById(businessId!),
    enabled: !!businessId,
  })

  const { data: businessFields = [] } = useQuery({
    queryKey: ['customFields', 'BUSINESS'],
    queryFn: () => customFieldsApi.getAll('BUSINESS'),
  })

  const [formData, setFormData] = useState<CreateBusinessRequest>({
    business_name: '',
    business_email: '',
    currency: 'USD',
    custom_fields: {},
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Load existing business data
  useEffect(() => {
    if (existingBusiness && businessId) {
      const customFieldsData: Record<string, string> = Object.entries(existingBusiness.custom_fields || {}).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: value.field_value || '' }),
        {} as Record<string, string>
      )

      setFormData({
        business_name: existingBusiness.business_name,
        business_email: existingBusiness.business_email,
        business_phone: existingBusiness.business_phone,
        business_address_line1: existingBusiness.business_address_line1,
        business_address_line2: existingBusiness.business_address_line2,
        business_city: existingBusiness.business_city,
        business_state: existingBusiness.business_state,
        business_postal_code: existingBusiness.business_postal_code,
        business_country: existingBusiness.business_country,
        currency: existingBusiness.currency,
        business_tax_id: existingBusiness.business_tax_id,
        business_registration_no: existingBusiness.business_registration_no,
        industry: existingBusiness.industry,
        website: existingBusiness.website,
        custom_fields: customFieldsData,
      })

      // Validate loaded custom fields
      const errors: Record<string, string> = {}
      businessFields.forEach((field) => {
        const value = customFieldsData[field.config_id] || ''
        const error = validateCustomField(field, value)
        if (error) {
          errors[field.config_id] = error
        }
      })
      setFieldErrors(errors)
    }
  }, [existingBusiness, businessId, businessFields])

  const createMutation = useMutation({
    mutationFn: (data: CreateBusinessRequest) => businessesApi.create(clientId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      toast.success('Business created successfully')
      navigate(`/clients/${clientId}/businesses`)
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(error, 'Failed to create business')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateBusinessRequest) => businessesApi.update(businessId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      queryClient.invalidateQueries({ queryKey: ['business', businessId] })
      toast.success('Business updated successfully')
      setIsEditing(false)
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(error, 'Failed to update business')
      toast.error(errorMessage)
    },
  })

  const validateCustomField = (field: FieldConfiguration, value: string): string | null => {
    if (field.is_mandatory && !value) {
      return `${field.field_label} is required`
    }
    if (field.regex_pattern && value && !validateRegex(value, field.regex_pattern)) {
      return `${field.field_label} must match pattern: ${field.regex_pattern}`
    }
    if (field.data_type === 'NUMBER' && value && isNaN(Number(value))) {
      return `${field.field_label} must be a number`
    }
    if (field.data_type === 'DATE' && value) {
      // Validate date format - must be YYYY-MM-DD or other accepted formats
      const dateFormats = [
        /^\d{4}-\d{2}-\d{2}$/,                    // YYYY-MM-DD
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/, // ISO format
        /^\d{2}\/\d{2}\/\d{4}$/,                  // MM/DD/YYYY
        /^\d{2}-\d{2}-\d{4}$/,                    // DD-MM-YYYY
      ]
      const isValidFormat = dateFormats.some(format => format.test(value))
      if (!isValidFormat) {
        return `${field.field_label} must be in format YYYY-MM-DD`
      }
      // Also check if it's a valid date
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return `${field.field_label} must be a valid date`
      }
    }
    return null
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate email format
    if (formData.business_email && !isValidEmail(formData.business_email)) {
      toast.error('Please enter a valid email address')
      const element = document.getElementById('business_email')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.focus()
      }
      return
    }

    // Validate all custom fields (mandatory and regex)
    const errors: Record<string, string> = {}
    businessFields.forEach((field) => {
      const value = formData.custom_fields?.[field.config_id] || ''
      const error = validateCustomField(field, value)
      if (error) {
        errors[field.config_id] = error
      }
    })

    // Also check existing fieldErrors from real-time validation
    const allErrors = { ...fieldErrors, ...errors }

    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors)
      // Scroll to first error field
      const firstErrorField = Object.keys(allErrors)[0]
      const element = document.getElementById(firstErrorField)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.focus()
      }
      return
    }

    if (isCreateMode) {
      createMutation.mutate(formData)
    } else if (businessId) {
      updateMutation.mutate(formData as UpdateBusinessRequest)
    }
  }

  const handleCustomFieldChange = (configId: string, value: string, field: FieldConfiguration) => {
    setFormData({
      ...formData,
      custom_fields: { ...formData.custom_fields, [configId]: value },
    })

    const error = validateCustomField(field, value)
    setFieldErrors((prev) => {
      if (error) {
        return { ...prev, [configId]: error }
      }
      const { [configId]: _, ...rest } = prev
      return rest
    })
  }

  const isFormValid = () => {
    if (!formData.business_name || !formData.business_email || !formData.currency) return false
    if (Object.keys(fieldErrors).length > 0) return false

    for (const field of businessFields) {
      if (field.is_mandatory && !formData.custom_fields?.[field.config_id]) {
        return false
      }
    }
    return true
  }

  const pageTitle = isCreateMode ? 'Add New Business' : (isEditing ? 'Edit Business' : 'Business Details')
  const pageDescription = isCreateMode
    ? `Add a new business for ${client?.client_name || 'client'}`
    : `${isEditing ? 'Update' : 'View'} business information`

  if (isLoadingBusiness && businessId) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">Client not found</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/clients/${clientId}/businesses`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            <p className="text-muted-foreground">{pageDescription}</p>
          </div>
        </div>
        {businessId && !isEditing && (
          <Button onClick={() => setIsEditing(true)} variant="outline">
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Business Information</CardTitle>
            <CardDescription>
              {isCreateMode ? 'Enter the business details below' : 'Business details and information'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  {isEditing ? (
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Enter business name"
                      required
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_email">Email *</Label>
                  {isEditing ? (
                    <Input
                      id="business_email"
                      type="email"
                      value={formData.business_email}
                      onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                      placeholder="business@example.com"
                      required
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_phone">Phone</Label>
                  {isEditing ? (
                    <Input
                      id="business_phone"
                      value={formData.business_phone || ''}
                      onChange={(e) => setFormData({ ...formData, business_phone: sanitizePhoneNumber(e.target.value) })}
                      placeholder="+1 234 567 8900"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_phone || '-'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency *</Label>
                  {isEditing ? (
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((currency) => (
                          <SelectItem key={currency.code} value={currency.code}>
                            {currency.code} - {currency.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm py-2">{formData.currency}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  {isEditing ? (
                    <Input
                      id="industry"
                      value={formData.industry || ''}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Technology"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.industry || '-'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  {isEditing ? (
                    <Input
                      id="website"
                      value={formData.website || ''}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  ) : (
                    <p className="text-sm py-2">
                      {formData.website ? (
                        <a href={formData.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {formData.website}
                        </a>
                      ) : '-'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Address Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_address_line1">Address Line 1</Label>
                  {isEditing ? (
                    <Input
                      id="business_address_line1"
                      value={formData.business_address_line1 || ''}
                      onChange={(e) => setFormData({ ...formData, business_address_line1: e.target.value })}
                      placeholder="Street address"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_address_line1 || '-'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_address_line2">Address Line 2</Label>
                  {isEditing ? (
                    <Input
                      id="business_address_line2"
                      value={formData.business_address_line2 || ''}
                      onChange={(e) => setFormData({ ...formData, business_address_line2: e.target.value })}
                      placeholder="Apartment, suite, etc."
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_address_line2 || '-'}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_city">City</Label>
                  {isEditing ? (
                    <Input
                      id="business_city"
                      value={formData.business_city || ''}
                      onChange={(e) => setFormData({ ...formData, business_city: e.target.value })}
                      placeholder="City"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_city || '-'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_state">State</Label>
                  {isEditing ? (
                    <Input
                      id="business_state"
                      value={formData.business_state || ''}
                      onChange={(e) => setFormData({ ...formData, business_state: e.target.value })}
                      placeholder="State"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_state || '-'}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_postal_code">Postal Code</Label>
                  {isEditing ? (
                    <Input
                      id="business_postal_code"
                      value={formData.business_postal_code || ''}
                      onChange={(e) => setFormData({ ...formData, business_postal_code: e.target.value })}
                      placeholder="Postal code"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_postal_code || '-'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_country">Country</Label>
                  {isEditing ? (
                    <Input
                      id="business_country"
                      value={formData.business_country || ''}
                      onChange={(e) => setFormData({ ...formData, business_country: e.target.value })}
                      placeholder="Country"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_country || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tax Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Tax & Registration Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_tax_id">Tax ID</Label>
                  {isEditing ? (
                    <Input
                      id="business_tax_id"
                      value={formData.business_tax_id || ''}
                      onChange={(e) => setFormData({ ...formData, business_tax_id: e.target.value })}
                      placeholder="Tax identification number"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_tax_id || '-'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_registration_no">Registration Number</Label>
                  {isEditing ? (
                    <Input
                      id="business_registration_no"
                      value={formData.business_registration_no || ''}
                      onChange={(e) => setFormData({ ...formData, business_registration_no: e.target.value })}
                      placeholder="Business registration number"
                    />
                  ) : (
                    <p className="text-sm py-2">{formData.business_registration_no || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Custom Fields */}
            {businessFields.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Custom Fields</h3>
                <div className="grid grid-cols-2 gap-4">
                  {businessFields.map((field) => (
                    <div key={field.config_id} className="space-y-2">
                      <Label htmlFor={field.config_id}>
                        {field.field_label} {field.is_mandatory && '*'}
                      </Label>
                      {isEditing ? (
                        <>
                          {field.data_type === 'DROPDOWN' ? (
                            <Select
                              value={formData.custom_fields?.[field.config_id] || ''}
                              onValueChange={(value) => handleCustomFieldChange(field.config_id, value, field)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Select ${field.field_label}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.dropdown_options?.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id={field.config_id}
                              type={field.data_type === 'NUMBER' ? 'number' : 'text'}
                              value={formData.custom_fields?.[field.config_id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.config_id, e.target.value, field)}
                              className={fieldErrors[field.config_id] ? 'border-destructive focus-visible:ring-destructive' : ''}
                            />
                          )}
                          {fieldErrors[field.config_id] && (
                            <p className="text-sm text-destructive font-medium">{fieldErrors[field.config_id]}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm py-2">
                          {formData.custom_fields?.[field.config_id] || '-'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Form Actions */}
            {isEditing && (
              <div className="flex justify-center gap-4 pt-6 border-t">
                {businessId ? (
                  <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                ) : (
                  <Link to={`/clients/${clientId}/businesses`}>
                    <Button type="button" variant="outline">Cancel</Button>
                  </Link>
                )}
                <Button type="submit" disabled={!isFormValid() || createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' :
                   updateMutation.isPending ? 'Saving...' :
                   (isCreateMode ? 'Create Business' : 'Save Changes')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </div>
  )
}

